import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, Instagram, ArrowRight, Facebook } from 'lucide-react';
import { cn } from '../lib/utils';
import { motion } from 'framer-motion';

export const Footer = () => {
  const location = useLocation();
  const pathname = location.pathname;

  const brands = {
    '/kalyan-agencies': { name: 'Kalyan Agencies', color: 'text-kalyan-primary', border: 'border-kalyan-primary' },
    '/frostella-pickles': { name: 'Frostella Pickles', color: 'text-frostella-pickles-primary', border: 'border-frostella-pickles-primary' },
    '/frostella-desserts': { name: 'Frostella Desserts', color: 'text-frostella-desserts-primary', border: 'border-frostella-desserts-primary' },
    '/prime-learn-academy': { name: 'Prime Learn', color: 'text-prime-primary', border: 'border-prime-primary' },
    '/labathome': { name: 'Lab @ Home', color: 'text-labathome-primary', border: 'border-labathome-primary' },
  };

  const currentBrandKey = Object.keys(brands).find(key => pathname.includes(key));
  const currentBrand = currentBrandKey ? brands[currentBrandKey as keyof typeof brands] : null;

  return (
    <footer className="bg-primary text-white border-t border-white/10">

      {/* Brand Specific Pre-Footer - Keep Light to differentiate? No, let's make it stand out or integrate. 
          Let's keep it clean: A small banner if active.
      */}
      {currentBrand && (
        <div className="py-3 bg-white border-b border-neutral-200 text-center">
          <p className="text-xs uppercase tracking-widest text-neutral-500">
            You are viewing <span className={cn("font-bold", currentBrand.color)}>{currentBrand.name}</span>
          </p>
        </div>
      )}

      <div className="max-w-[95%] mx-auto pt-16 md:pt-24 pb-8 md:pb-12 px-6">

        {/* Main Footer Grid */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10 md:gap-16 mb-16 md:mb-24">

          {/* Identity */}
          <div className="col-span-2 md:col-span-4 space-y-6 md:space-y-8">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-white rounded-lg">
                <img
                  src="/images/millennium-logo.webp"
                  alt="Millennium Group"
                  className="w-8 h-8 md:w-10 md:h-10 object-contain"
                  width="40"
                  height="40"
                />
              </div>
              <div>
                <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight leading-none text-white">Millennium<br />Group Of Companies</h2>
              </div>
            </div>
            <p className="text-base md:text-lg text-neutral-200 font-light max-w-sm leading-relaxed">
              Quality without compromise. Rooted in North Kerala, serving with integrity since 2015.
            </p>
          </div>

          {/* Links Column 1 */}
          <div className="col-span-1 md:col-span-2 md:col-start-7">
            <div className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-accent mb-6 md:mb-8" role="heading" aria-level={3}>Explore</div>
            <ul className="space-y-4 md:space-y-5">
              <li><Link to="/" className="text-neutral-200 hover:text-white transition-colors text-sm font-medium">Home</Link></li>
              <li><Link to="/about" className="text-neutral-200 hover:text-white transition-colors text-sm font-medium">About Us</Link></li>
              <li><Link to="/companies" className="text-neutral-200 hover:text-white transition-colors text-sm font-medium">Group Companies</Link></li>
              <li><Link to="/contact" className="text-neutral-200 hover:text-white transition-colors text-sm font-medium">Contact</Link></li>
            </ul>
          </div>

          {/* Links Column 2 */}
          <div className="col-span-1 md:col-span-2">
            <div className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-accent mb-6 md:mb-8" role="heading" aria-level={3}>Ventures</div>
            <ul className="space-y-4 md:space-y-5">
              <li><Link to="/kalyan-agencies" className="text-neutral-200 hover:text-[#D4A853] transition-colors text-sm font-medium">Kalyan Agencies</Link></li>
              <li><Link to="/frostella-pickles" className="text-neutral-200 hover:text-[#D4A853] transition-colors text-sm font-medium">Frostella Pickles</Link></li>
              <li><Link to="/frostella-desserts" className="text-neutral-200 hover:text-[#D4A853] transition-colors text-sm font-medium">Frostella Desserts</Link></li>
              <li><Link to="/prime-learn-academy" className="text-neutral-200 hover:text-[#D4A853] transition-colors text-sm font-medium">Prime Learn Academy</Link></li>
              <li><Link to="/medicity" className="text-neutral-200 hover:text-[#D4A853] transition-colors text-sm font-medium">Lab @ Home</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div className="col-span-2 md:col-span-2">
            <div className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-accent mb-6 md:mb-8" role="heading" aria-level={3}>Connect</div>
            <ul className="space-y-4 md:space-y-5">
              <li>
                <a href="tel:+917025865774" className="group flex items-center gap-3 text-neutral-200 hover:text-white transition-colors text-sm font-medium">
                  <Phone className="w-4 h-4 text-accent" />
                  <span>Call Us</span>
                </a>
              </li>
              <li>
                <a href="mailto:millenniumgroup.official.in@gmail.com" className="group flex items-center gap-3 text-neutral-200 hover:text-white transition-colors text-sm font-medium">
                  <Mail className="w-4 h-4 text-accent" />
                  <span>Email Us</span>
                </a>
              </li>
              <li>
                <a href="https://instagram.com/millennium_group_of_companies" target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3 text-neutral-200 hover:text-white transition-colors text-sm font-medium">
                  <Instagram className="w-4 h-4 text-accent" />
                  <span>Instagram</span>
                </a>
              </li>
              <li>
                <a href="https://www.facebook.com/people/Millennium-Group-Of-Companies/61587052833791/" target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3 text-neutral-200 hover:text-white transition-colors text-sm font-medium">
                  <Facebook className="w-4 h-4 text-accent" />
                  <span>Facebook</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Big Brand Text */}
        <div className="border-t border-white/10 pt-12 overflow-hidden" aria-hidden="true">
          <motion.h1
            initial={{ opacity: 0.5, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display font-bold text-[13vw] leading-none text-white/5 text-center select-none tracking-tighter"
          >
            MILLENNIUM
          </motion.h1>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center text-xs text-neutral-500 mt-12 border-t border-white/5 pt-8">
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
            <p>© 2026 Millennium Group. All rights reserved.</p>
            <span className="hidden md:block w-px h-3 bg-white/10" />
            <p className="flex items-center gap-1">
              Crafted by <a href="https://formastdio.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors underline decoration-white/20 underline-offset-4">Forma Studio</a>
            </p>
          </div>
          <div className="flex gap-8 mt-4 md:mt-0">
            <Link to="/privacy-policy" className="hover:text-white cursor-pointer transition-colors">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="hover:text-white cursor-pointer transition-colors">Terms of Use</Link>
          </div>
        </div>

      </div>
    </footer>
  );
};